import { defineStore } from 'pinia';
import { LocalStorage, QForm } from 'quasar';
import type { dynamicObj } from 'src/pages/assets';
import {
  docFieldsApiService,
  supplierApiService,
  supplierDataApiService,
} from 'src/services';
import { SupplierStatus, SupplierStep } from 'src/supplier/types';
import { ArrayError, flattenObjForDynamic } from 'src/supplier/utils';
import { markRaw, reactive, ref } from 'vue';
export type SupplierForm = {
  [key: string]: QForm | null;
};
import { HtmlContent } from 'src/supplier/types';
import {
  isManagementUser,
  isSupplierUser,
  isTopLevelUserAdmin,
} from 'src/utils/UserDetails';
import { ApiError } from 'src/errors';
import { ApiExceptions } from 'src/exceptions';
import { supplierDocumentType } from 'src/pages/Settings/SupplierLayouts/formBuilderConstants';
import { getOrgId } from 'src/utils/organization/org_id_helper';
import { listOfValuesApiService } from 'src/services';
import { useDropdownStore } from 'src/store/main';
import { DEFAULT_LANGUAGE } from 'src/constants';
import { notify } from 'src/components/notifications/notify';
import RepeatableSectionAction from 'src/supplier/components/dynamic/form/RepeatableSectionAction.vue';

export const useSupplierLayoutsStore = defineStore('supplier_layouts', {
  state: () => ({
    keyToUpdateComponnets: 0,
    activeTab: 'form_elements',
    defaultFields: [] as any,
    defaultGroups: [] as any,
    formLayout: [] as any,
    isDefaultFieldLoaded: false,
    activeFieldName: '',
    activeGroupIndex: null as null | number,
    docType: '',
    allowAreaSelection: false,
    renderComponent: false,
    model: ref({} as dynamicObj),
    propertiesType: '',
    isBefore: ref(reactive(new Map())),
    document_types: [] as any[],
    loading: ref(false),
    layout_type: ref(''),
    step: ref<SupplierStep>(0),
    INIT_STEP: 0 as number,
    formConfigure: ref({}) as dynamicObj,
    supplierUploadStatus: ref({}) as any,
    supplierError: reactive<dynamicObj>({}),
    supplierForm: reactive<SupplierForm>({}),
    listOfValues: [] as any[],
    //data alisting for supplier page
    status: ref<SupplierStatus>('pending_registration'),
    last_updated_at: ref<string>(''),
    requester_id: ref<string>(''),
    assigned_user_id: ref<string>(''),
    supplierDetailsErrors: ref<string[]>([]),
    supplier_number: ref(''),
    isFromClickSave: ref(false),
    isChangeDetected: ref(false),
    keysToCheck: [
      'calculation_formula',
      'is_hidden',
      'list_of_values',
      'title',
      'erp_field_name',
    ],
    originalFormMultiple: new Map(),
    activeSupplierSidebar: ref<'' | 'chat' | 'email'>(''),
    _getFieldsPromise: null as Promise<void> | null,

  }),
  getters: {},
  actions: {
    badgeColor(status: string) {
      switch (status) {
        case 'pending_approval':
        case 'pending_ap_approval':
        case 'pending_lt_approval':
          return 'warning';
        case 'pending_registration':
        case 'approved':
        case 'pending_supplier_input':
          return 'positive';
        case 'rejected':
          return 'negative';
        default:
          return 'grey';
      }
    },
    async CheckSupplierChatOnline(supplier_as_org_id: any): Promise<void> {
      await supplierDataApiService.CheckSupplierOnline(supplier_as_org_id);
    },
    async getFields() {
      // Deduplicate concurrent calls — reuse in-flight promise
      if (this._getFieldsPromise) {
        return this._getFieldsPromise;
      }

      //use cache data in the pinia store if already loaded before
      if (this.defaultFields.length > 0) {
        return;
      }

      this._getFieldsPromise = this._fetchFields();
      try {
        await this._getFieldsPromise;
      } finally {
        this._getFieldsPromise = null;
      }
    },
    async _fetchFields() {
      //ToDo: Remove this check after prod push to accomodate the new language changes
      const dropdown_store = useDropdownStore();
      const selectedLang =
        dropdown_store.getSelectedLanguage() === 'en'
          ? DEFAULT_LANGUAGE
          : dropdown_store.getSelectedLanguage();

      if (this.listOfValues.length === 0) {
        const result = await listOfValuesApiService.getLists(true);
        if(!result.success){
          throw new Error('Failed to get list of values');
        }
        this.listOfValues = result.data;
      }
      await this.getDetaultFields();
      this.defaultFields.map((field: dynamicObj) => {
          if(field.field_type === 'SELECT' || field.field_type === 'SEARCH' || field.field_type === 'RADIO'){
              const result = this.listOfValues.find((item: any) => item.label	 === field.list_of_values);
              if(result){
                field.options = result.values.map((item: any) => {
                  const matchedLang = item.label?.find((l: any) => l.language === selectedLang);
                  return {
                    label: matchedLang?.translated_value ?? item.label,
                    value: item.value
                  }
                });
              }
          }
      });
    },
    async getDetaultFields() {
      this.defaultFields = [];
      const data = {
        doc_type: 'SUPPLIER_M3',
      };
      try {
        const response = await docFieldsApiService.getFieldSettingsByType(data);
        if (response.success === false) {
          throw response;
        }
        Array.from(response.field_setting[0].rows).forEach((rows: any) => {
          Array.from(rows).forEach((row: any) => {
            row.isSearched = true;
            // Transform raw field name to translation key if not already a translation key
            if (row.field_name && (!row.title || !row.title.includes('.'))) {
              row.title = `supplier_register_form.field.${row.field_name}`;
            }
            this.defaultFields.push(row);
          });
        });
      } catch (err: any) {
        throw err;
      }
    },
    async getSupplierType() {
      try {
        const response = await supplierApiService.getSupplierLayout();
        if (!response) {
          throw response;
        }
        this.document_types = response?.data || [];
      } catch (err: any) {
        logError(err);
      }
    },
    async detectChange(json: dynamicObj[]) {
      await this.getFields();
      this.isChangeDetected = json.some((group: any) => {
        return group.rows.some((row: any) => {
          const existingField = this.defaultFields.find(
            (field: any) => field.field_name === row.field_name,
          );
          if (existingField) {
            return this.keysToCheck.some((key) => {
              if (key === 'list_of_values') {
                return (
                  JSON.stringify(row['options']) !==
                  JSON.stringify(existingField['options'])
                );
              }
              return row[key] !== existingField[key];
            });
          }
          return false;
        });
      });
    },
    syncPropertiesFields(json: dynamicObj[]) {
      json?.map((group: any) => {
        group?.rows.map((row: any) => {
          const existingField = this.defaultFields.find(
            (field: any) => field.field_name === row.field_name,
          );
          if (existingField) {
            for (const key of this.keysToCheck) {
              if (!row[key] || row?.[key] != existingField?.[key]) {
                row[key] = existingField[key];
              }
              if (key == 'list_of_values') {
                if (existingField['options']) {
                  const formattedOptions: { label: string; value: number }[] =
                    [];
                  existingField['options'].forEach(
                    (option: any, index: any) => {
                      formattedOptions[index] = {
                        label: option.label[0].translated_value ?? option.label,
                        value: option.value,
                      };
                    },
                  );
                  row['options'] = formattedOptions;
                } else {
                  row['options'] = existingField['options'];
                }
              }
            }
          }
        });
      });
      this.formLayout = json;
    },
    /**
     * Transform raw field titles to translation keys for supplier fields.
     * Converts raw field names like "city" to "supplier_register_form.field.city"
     */
    transformFieldTitles(jsonObj: dynamicObj[]): void {
      jsonObj.forEach((group: any) => {
        // Transform group_name to translation key if not already a translation key
        if (group.group_name && !group.group_name.includes('.')) {
          group.group_name = `supplier_register_form.field.${group.group_name}`;
        }
        if (group.rows && Array.isArray(group.rows)) {
          group.rows.forEach((row: any) => {
            // Transform raw field name to translation key if not already a translation key
            if (row.field_name && (!row.title || !row.title.includes('.'))) {
              row.title = `supplier_register_form.field.${row.field_name}`;
            }
          });
        }
      });
    },
    async getSupplierLayoutForBuilder(id: any): Promise<any> {
      this.formLayout = [];
      try {
        const response =
          await supplierApiService.getSupplierLayoutBYIDforBuilder(id);
        if (!response) {
          throw response;
        }
        this.layout_type = response?.data?.layout_type;
        const jsonData = JSON.parse(response?.data?.layout_json);
        const jsonObj = JSON.parse(jsonData) as dynamicObj[];
        // Transform field titles to translation keys
        this.transformFieldTitles(jsonObj);
        this.formLayout = jsonObj || [];
        await this.detectChange(jsonObj);
      } catch (err: any) {
        logError(err);
      }
    },
    async getSuppierLayoutByID(id: any, parent_org_id: any): Promise<any> {
      const data = {} as any;
      if (id === 'supplier_user') {
        data['parent_org_id'] = parent_org_id;
      }
      try {
        const response = await supplierApiService.getSupplierLayoutBYID(
          id,
          data,
        );
        if (!response) {
          throw response;
        }
        this.layout_type = response?.data?.layout_type;
        const jsonData = JSON.parse(response?.data?.layout_json);
        const jsonObj = JSON.parse(jsonData) as dynamicObj[];
        // Transform field titles to translation keys
        this.transformFieldTitles(jsonObj);
        this.formLayout = jsonObj || [];
        this.populateForm();
      } catch (err: any) {
        logError(err);
      }
    },
    async applyDefaultTemplate(id: string): Promise<any> {
      this.loading = true;
      try {
        const response = await supplierApiService.getDefaultTemplate(id);
        if (!response.success) {
          throw response;
        }
        this.layout_type = response?.data?.layout_type;

        const jsonData = JSON.parse(response?.data?.layout_json);
        this.formLayout = JSON.parse(jsonData) || [];
      } catch (err: any) {
        throw err;
      } finally {
        this.loading = false;
      }
    },
    addMultipleForm(): HtmlContent {
      return {
        component: markRaw(RepeatableSectionAction),
        props: {
          class: 'supplier-repeatable-action',
        },
      };
    },
    populateForm() {
      this.formLayout.forEach((group: any, groupIndex: number) => {
        if (group.after) {
          if (group.rows.length > 0) {
            this.formLayout[groupIndex].rows[0].after = this.addMultipleForm();
            // change to use originalFormMultiple variable due when the metadata set properties to fields
            //  it will effect to the function click add new row (in case multiple section )
            this.originalFormMultiple.set(group.name, group.rows);
            this.formLayout[groupIndex].rows = [group.rows];
          }
        }
      });
      this.formLayout.forEach((group: any) => {
        if (group.after) {
          this.formConfigure[group.name] = [];
          group.rows.forEach((row: any, index: number) => {
            this.formConfigure[group.name][index] = {};
            row.forEach((element: any) => {
              if (
                element.hasOwnProperty('default_value') &&
                element.default_value !== undefined &&
                element.default_value !== null
              ) {
                this.formConfigure[group.name][index][element.field_name] =
                  element.default_value;
              }
            });
          });
        } else {
          this.formConfigure[group.name] = {};
          group.rows.forEach((element: any) => {
            if (
              element.hasOwnProperty('default_value') &&
              element.default_value !== undefined &&
              element.default_value !== null
            ) {
              this.formConfigure[group.name][element.field_name] =
                element.default_value;
            }
          });
        }
      });
    },
    toggle(index: SupplierStep) {
      if (this.step === index + 1) {
        this.step = 0;
      } else {
        this.goto(index + 1);
      }
    },
    async next() {
      if (this.step < this.formLayout.length + 1) {
        const valid = this.validateForm(this.step);
        if (!(await valid)) return;
        if (this.formLayout[this.step].hidden) {
          this.step++;
          await this.next();
        } else {
          this.step++;
        }
      }
    },

    prev() {
      if (this.step > this.INIT_STEP) {
        if (this.formLayout[this.step - 2].hidden) {
          this.step--;
          this.prev();
        } else {
          this.step--;
        }
      }
    },

    goto(index: SupplierStep) {
      if (index > this.INIT_STEP && index < this.formLayout.length + 1) {
        this.step = index;
      }
    },
    async validateForm(index: number) {
      if (index === 0 || index > this.formLayout.length + 1) return true;

      const currentStep = this.formLayout[index - 1].name;
      const form = this.supplierForm[currentStep];
      if (!form) return true;
      this.supplierError[currentStep] = 0;
      await Promise.all(
        form.getValidationComponents().map(async (field: any) => {
          field.resetValidation();
          const valid = await field.validate();
          if (!valid) {
            this.supplierError[currentStep]++;
          }
        }),
      );
      await form.validate(true);

      this.displayErrorBadge();
      return this.supplierError[currentStep] === 0;
    },
    displayErrorBadge() {
      const qStepperElements =
        document.querySelectorAll<HTMLElement>('.q-stepper__tab');
      if (!qStepperElements) return;
      const visibleSteps = this.formLayout.filter((step: any) => !step.hidden);

      qStepperElements.forEach((el, index) => {
        const key = visibleSteps[index]?.name;
        if (!key) return;
        const errorCount = this.supplierError[key];
        const qStepDot = el.querySelector('.q-stepper__dot');
        if (!qStepDot) return;
        this.addBadge(qStepDot, errorCount);
      });
    },
    addBadge(el: Element, errorCount: number) {
      el.querySelector('.q-badge')?.remove();
      if (errorCount === -1) return;

      const status = errorCount > 0 ? 'error' : 'success';
      const badge = document.createElement('div');
      badge.classList.add(
        'q-badge',
        'flex',
        'inline',
        'items-center',
        'no-wrap',
        'q-badge--single-line',
        'q-badge--floating',
        'q-badge--rounded',
      );
      badge.setAttribute('role', 'status');
      if (status === 'error') {
        badge.classList.add('bg-red');
        badge.textContent = errorCount.toString();
      } else {
        badge.classList.add('bg-green');
        badge.textContent = '✓';
      }
      el.appendChild(badge);
    },
    async validateAllForm() {
      const promises: Promise<boolean>[] = [];
      for (let i = 1; i < this.formLayout.length + 1; i++) {
        promises.push(this.validateForm(i)); // Directly push the promise
      }

      // Check all validation results
      const results = await Promise.all(promises);
      return results.every((result) => result);
    },
    async uploadFile(id: string, file: File, type: string) {
      try {
        return await supplierDataApiService.uploadFile(id, file, type);
      } catch (error) {
        throw error;
      }
    },
    async getSupplierFile(file_path: string) {
      return await supplierDataApiService
        .getSupplierDetailFile(file_path)
        .catch((err: any) => {
          const msg = ApiExceptions.getErrorMessage(err as dynamicObj);
          // notify.error(msg)
        });
    },
    async deleteFile(id: string, type: string) {
      return await supplierDataApiService
        .deleteFile(id, type)
        .catch((err: any) => {
          return err as dynamicObj;
        });
    },
    onClickActionButton(index: number, key: string) {
      const group = this.formLayout.find((item: any) => item.name === key);
      if (group) {
        const arrfield = this.originalFormMultiple.get(key);
        const fieldForm = this.formConfigure[key];
        if (index === 0) {
          fieldForm.push({});
          group.rows.push(arrfield);
        } else {
          group.rows.splice(index, 1);
          fieldForm.splice(index, 1);
        }
      }
    },
    checkExistingVendor(oldId: string) {
      if (
        (this.supplier_number !== null ||
          this.supplier_number !== undefined ||
          this.supplier_number !== '') &&
        oldId !== 'supplier_lookup_record_id' &&
        oldId !== ''
      ) {
        this.formConfigure['is_existing_vendor'] = true;
      } else {
        this.formConfigure['is_existing_vendor'] = false;
      }
    },
    async fetchOrgSupplier(
      oldId = 'supplier_lookup_record_id',
      id = 'supplier_as_org_id',
    ): Promise<any> {
      await supplierDataApiService
        .getOrgSupplierDetails(oldId, id)
        .then(async (res) => {
          if (!res.success) throw new Error(res as any);
          this.supplier_number = res.data?.supplier_header?.supplier_number;
          this.checkExistingVendor(oldId);
          await this.handlingFetchData(res.data);
        })
        .catch((err) => {
          throw err.message;
        });
    },
    async fetchOrgSupplierWizard(
      oldId = 'supplier_lookup_record_id',
      id = 'supplier_as_org_id',
      parent_ord_id: string,
    ): Promise<any> {
      await supplierDataApiService
        .getOrgSupplierDetails(oldId, id)
        .then(async (res) => {
          if (!res.success) throw new Error(res as any);
          const orgKey = '_is_from_click_save_' + getOrgId();
          this.isFromClickSave = res.data[orgKey];
          this.supplier_number = res.data?.supplier_header?.supplier_number;
          this.checkExistingVendor(oldId);
          if (
            isSupplierUser() &&
            !this.isFromClickSave &&
            oldId !== '' &&
            oldId !== 'supplier_lookup_record_id' &&
            id === getOrgId()
          ) {
            await this.handleFetchForWizard(res.data, oldId, id, parent_ord_id);
          } else {
            await this.handlingFetchData(res.data);
          }
        })
        .catch((err) => {
          throw err.message;
        });
    },
    async handleFetchForWizard(
      response: dynamicObj,
      supplier_lookup_record_id: string,
      supplier_as_org_id: string,
      parent_ord_id: string,
    ): Promise<void> {
      const data = {
        parent_org_id: parent_ord_id,
        supplier_number: this.supplier_number,
        supplier_lookup_record_id: supplier_lookup_record_id,
        supplier_as_org_id: supplier_as_org_id,
      };
      try {
        const rs = (await supplierDataApiService.syncDatafromM3(data)) as any;
        if (rs.success === false) {
          await this.handlingFetchData(response);
          throw rs;
        }
        const syncData = rs.data as dynamicObj;
        for (const key in syncData) {
          if (!this.checkResponse(response[key])) {
            response[key] = syncData[key];
          }
        }
        await this.handlingFetchData(response);
      } catch (error) {
        await this.handlingFetchData(response);
        throw error;
      }
    },
    checkResponse(response: any): boolean {
      let isValid = true;
      if (Array.isArray(response) && response.length == 0) {
        isValid = false;
      } else if (!response || response === null || response === undefined) {
        isValid = false;
      }
      return isValid;
    },
    populateFormWithMultipleChild(
      field: any,
      key: string,
      data: dynamicObj,
    ): dynamicObj[] {
      const list_data: dynamicObj[] = [];
      const row = field.rows ? field.rows[0] : [];
      const required_key: string[] = [];
      row.forEach((row_field: any) => {
        required_key.push(row_field.field_name);
      });
      for (const [dataKey, dataValue] of Object.entries(data)) {
        if (!Array.isArray(dataValue)) {
          continue;
        }
        for (const data_el of dataValue) {
          for (const [data_el_key, data_el_val] of Object.entries(data_el)) {
            if (required_key.includes(data_el_key)) {
              list_data.push(data_el);
              break;
            }
          }
        }
      }
      return list_data;
    },
    async handlingFetchData(data: dynamicObj) {
      try {
        if (!data) return;

        this.last_updated_at = data?.last_updated_at;
        this.requester_id = data?.supplierDetails?.requesterId;
        this.assigned_user_id = String(
          data?.supplierDetails?.assignedUser ??
            data?.assigned_user ??
            '',
        );

        if (data.status) {
          const isAdmin =
            this.layout_type === supplierDocumentType.SUPPLIER_MANAGEMENT;
          if (!isAdmin && data.status === 'pending_ap_approval') {
            data.status = 'pending_approval';
            this.status = 'pending_approval' as SupplierStatus;
          }
          this.status = data.status;
        }

        for (const key in this.formConfigure) {
          const field = this.formLayout.find((item: any) => item.name === key);
          if (!field) {
            continue;
          }
          if (field.after == true) {
            this.mappingDataMultiple(data, field, key);
            continue;
          }

          const rowPromises = field.rows.map(async (row: any) => {
            if (row.type === 'file' || row.type === 'FILE') {
              await this.handleFetchFiles(row, key, data);
            } else {
              if (data[row.field_name] !== undefined) {
                this.formConfigure[key][row.field_name] = data[row.field_name];
              }
            }
          });
          await Promise.all(rowPromises);
        }
      } catch (error) {
        logError(error);
      }
    },
    mappingDataMultiple(data: dynamicObj, field: any, key: string) {
      let resultPopulate = this.populateFormWithMultipleChild(field, key, data);
      if (resultPopulate.length > 0) {
        this.formConfigure[key] = resultPopulate;
      } else if (data[key] && data[key].length > 0) {
        this.formConfigure[key] = data[key];
        resultPopulate = data[key];
      }
      const increaseNumber = resultPopulate.length;
      if (field.rows.length >= increaseNumber) {
        return;
      }
      resultPopulate.forEach((item: any, index: number) => {
        if (index + 1 < increaseNumber) {
          field.rows.push(this.originalFormMultiple.get(field.name));
        }
      });
    },
    async handleFetchFiles(row: any, key: string, data: dynamicObj) {
      const indexKey = row.field_name;
      const file = data[row.input_type];
      this.supplierUploadStatus[indexKey] = {
        status: 'none',
        errorMessage: [],
      };

      if (!file) return;
      if (!file.path) {
        data[key][indexKey] = null;
        return;
      }

      try {
        const res = await this.getSupplierFile(file.path);
        if (!res) return;

        this.formConfigure[key][indexKey] = new File(
          [res],
          file.document_name,
          { type: res.type },
        );
        this.supplierUploadStatus[indexKey].status = 'uploaded';

        if (!file.is_valid) {
          throw new ArrayError(JSON.parse(file.error_message));
        }
      } catch (e) {
        if (e instanceof ArrayError) {
          this.supplierUploadStatus[indexKey].status = 'error';
          this.supplierUploadStatus[indexKey].errorMessage = e.messages;
        } else {
          throw e;
        }
      }
    },
    async updateSupplier(id: any): Promise<any> {
      const userDetail: any = LocalStorage.getItem('user_details');
      Object.keys(this.formConfigure).forEach((formKey) => {
        const nestedObject = this.formConfigure[formKey];
        const nestedForm = this.formLayout.find(
          (item: any) => item.name === formKey,
        );
        if (nestedForm && nestedForm.after) {
          nestedForm.rows.map((row: any, index: number) => {
            row.map((field: any) => {
              if (['NOTE', 'FILE', 'file'].includes(field.type)) {
                delete nestedObject[index][field.field_name];
              }
            });
          });
        }
        if (nestedForm && !nestedForm.after) {
          nestedForm.rows.map((fields: any) => {
            if (['NOTE', 'FILE', 'file'].includes(fields.type)) {
              delete nestedObject[fields.field_name];
            }
          });
        }
      });
      const flattenData = flattenObjForDynamic(this.formConfigure);
      const data = flattenData;
      if (isTopLevelUserAdmin() || isManagementUser()) {
        data['supplier_as_org_id'] = id;
      } else {
        data['parent_org_id'] = userDetail?.organisations[0]?.parent_org_id;
      }
      if (data.hasOwnProperty('is_existing_vendor')) {
        delete data['is_existing_vendor'];
      }
      return await supplierDataApiService
        .updateSupplierData(data)
        .then((res) => {
          if (!res.success) throw new Error(res as any);
          return res;
        })
        .catch((e) => {
          throw new Error(e);
        });
    },
    async sendForApproval(data: any): Promise<any> {
      return await supplierDataApiService
        .sendForApproval(data)
        .then((res) => {
          if (!res.success) throw new Error(res.message);
        })
        .catch((err: ApiError) => {
          throw err;
        });
    },
    async approve(data: any): Promise<any> {
      try {
        const res = await supplierDataApiService.approve(data);
        if (!res.success) throw res;
      } catch (error) {
        throw error;
      }
    },
    async reject(data: any): Promise<any> {
      try {
        const res = await supplierDataApiService.reject(data);
        if (!res.success) throw res;
      } catch (error) {
        throw error;
      }
    },
  },
});
